<template>
    <div
        :class="{ 'w-screen h-screen overflow-hidden': isContentLoading }"
        class="app-view relative"
    >
        <div
            v-if="isContentLoading"
            :class="{'w-screen h-screen': !!loaderPage}"
            class="app-global-loader  overflow-hidden pb-2 fixed inset-0 z-999999 main-bg-light"
        >
            <div class="w-full h-full">
                <component :is="loaderPage" />
            </div>
            <div class="absolute top-0 left-0 right-0 min-h-10">
                <el-progress
                    class="progress-bar"
                    :percentage="contentLoadingPercentage"
                    text-inside
                />
            </div>
        </div>
        <router-view />
        <RedesignWidgetEditModal />
        <RedesignGlobalLoader />
        <GlobalConfirmModal />
        <AppNotifications />
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { setActiveToken } from '@/composables/useCurrentUserData'
import initRealTimeSdk from '@/plugins/initRealTimeSdk'
import useAppDataComposable from '@/composables/useAppDataComposable'
import { useRoute } from 'vue-router/composables'
import addTokenToUrl from '@/helpers/addTokenToUrl'
import {
    ROUTE_DASHBOARD_CREATION_NAME,
    ROUTE_DASHBOARD_SETTINGS_NAME,
    ROUTE_HOME_NAME,
    ROUTE_LAYOUT_SETTINGS_NAME
} from '@/router/routes'
import { Progress as ElProgress } from 'element-ui'
import PageLoaderComponents from '@/components/RedesignComponents/PageLoaders'
import AppNotifications from '@/components/RedesignComponents/Notify/Notifications.vue'
import GlobalConfirmModal from '@/components/RedesignComponents/Modal/GlobalConfirmModal.vue'
import RedesignGlobalLoader from '@/components/RedesignComponents/App/RedesignGlobalLoader.vue'
import RedesignWidgetEditModal from '@/components/RedesignComponents/Modal/RedesignWidgetEditModal.vue'
import {
    activeLayout,
    getLayoutColors,
    getLayoutFontSize,
    getLayoutFontSizeVariables,
    setupActiveLayout
} from '@/composables/globalData/useLayouts'
import { ActiveDashboard } from '@/composables/globalData/useDashoardsData'
import { clearSoundsQueue } from '@/composables/useSoundsStoreData'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PageLoaders: Record<string, any> = {
    [ROUTE_HOME_NAME]: PageLoaderComponents.DashboardLoader,
    [ROUTE_DASHBOARD_SETTINGS_NAME]: PageLoaderComponents.DashboardSettingsLoader,
    [ROUTE_LAYOUT_SETTINGS_NAME]: PageLoaderComponents.DashboardLayoutSettingsLoader,
    [ROUTE_DASHBOARD_CREATION_NAME]: PageLoaderComponents.DashboardCreationLoader
}

/* Composable */
const { setupDashboardData, contentLoadingPercentage } = useAppDataComposable
const route = useRoute()

/* Computed */
const isContentLoading = computed(() => contentLoadingPercentage.value < 100)
const loaderPage = computed(() => {
    return route?.name && (PageLoaders[route.name] || '')
})
const layout = computed(() => {
    return activeLayout.value
})
const layoutColors = computed(() => {
    return getLayoutColors(layout.value)
})
const layoutFontSize = computed(() => {
    return getLayoutFontSize(layout.value)
})
const activeDashboard = computed(() => {
    return ActiveDashboard.value
})

/* Methods */
async function maybeClearPersistedData () {
    try {
        await setActiveToken()
        await initRealTimeSdk()
    } catch (e) {
        console.error(e)
    }
}

async function initData () {
    await maybeClearPersistedData()
    await setupDashboardData()
}

function initMainColorsVars () {
    const colors = layoutColors.value

    for (const color in colors) {
        document.documentElement.style.setProperty('--' + color + '-color', colors[color])
    }
}

function initMainFontSizeVar () {
    const variables = Object.entries(getLayoutFontSizeVariables(layoutFontSize.value))

    for (const [ key, value ] of variables) {
        document.documentElement.style.setProperty(key, value)
    }
}

/* Mounted */
onMounted(() => {
    const timeout = 8 * 60 * 60 * 1000 // 8 hours

    setTimeout(() => {
        window.location.reload()
    }, timeout)
})
onBeforeUnmount(() => {
    clearSoundsQueue()
})

/* Watchers */
watch(
    () => route.name,
    (name) => {
        console.log('WATCH ROUTER')
        if (name) {
            addTokenToUrl(name, route.query)
        }
    },
    { immediate: true }
)
watch(
    layout,
    () => {
        initMainColorsVars()
        initMainFontSizeVar()
    },
    { immediate: true }
)
watch(
    activeDashboard,
    async (newVal, oldVal) => {
        clearSoundsQueue()
        if (newVal && oldVal !== newVal) {
            console.log('[WATCH APP]: activeDashboard')
            await setupActiveLayout()
        }
    }
)

/* Created */
initData()
</script>
